import type { Driver } from '../../../../types'
import { useEffect, useMemo, useState, useCallback } from 'react'
import { useQueryClient } from 'react-query'
import { useUserContext } from '../../../../context/UserProvider'
import { useContacts } from '../../../../hooks/useContacts'
import { DriversTable } from './DriversTable/DriversTable'
import Container from '../../../../components/Container'
import Fuse from 'fuse.js'
import { useLocation } from 'react-router-dom'

const options = {
  isCaseSensitive: false,
  threshold: 0.1,
  useExtendedSearch: false,
  keys: ['name', 'regNo'],
}

export const Drivers = () => {
  const queryClient = useQueryClient()
  const { chosenCompany, roleType } = useUserContext()
  const { contacts, isLoading, isRefetching } = useContacts()

  const { search } = useLocation()
  const nameFromQuery = useMemo(() => new URLSearchParams(search).get('name'), [search])

  const [pattern, setPattern] = useState('')

  const drivers = useMemo(() => {
    const list = Array.isArray(contacts) ? contacts : []

    return list.filter(
      (driver: any) => (driver?.type ?? driver?.contactType)?.toLowerCase() !== 'main contact',
    ) as Driver[]
  }, [contacts])

  const fuse = useMemo(() => new Fuse(drivers ?? [], options), [drivers])

  const filteredDrivers = useMemo(() => {
    if (!pattern) return drivers
    return fuse.search(pattern).map((r) => r.item)
  }, [drivers, fuse, pattern])

  const applySearch = useCallback((value: string) => {
    setPattern(value)
  }, [])

  useEffect(() => {
    if (!nameFromQuery) return
    applySearch(nameFromQuery)
  }, [nameFromQuery, applySearch])

  const onSearchChange = (value: string) => applySearch(value)
  const emptySearch = () => applySearch('')

  const onUpdateAfterEdit = async () => {
    emptySearch()

    await Promise.all([
      queryClient.invalidateQueries(['contacts']),
      queryClient.invalidateQueries(['carPark']),
    ])
  }

  if (roleType !== 2) {
    return null
  }

  return (
    <Container isLoading={isLoading || isRefetching}>
      <div className="text-2xl font-semibold mb-6">Ansatte</div>
      <DriversTable
        companyId={chosenCompany?.id}
        companyName={chosenCompany?.name}
        isLoading={isLoading || isRefetching}
        drivers={filteredDrivers}
        pattern={pattern}
        onSearchChange={onSearchChange}
        onEditDriverFinish={onUpdateAfterEdit}
      />
    </Container>
  )
}
